import React, { useContext, useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import Form from 'react-bootstrap/Form';
import { MdArrowBack, MdLibraryAdd, MdOutlineFileUpload, MdSave } from 'react-icons/md';
import { toast } from '../../toasts';
import { Store } from '../../Store';
import { Button, FormField, Section, Select, controlClass } from '../../components/EntityList';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import documentService from '../../services/document.service';
import userService from '../../services/user.service';
import { ROLE_OPTIONS } from '../../constants/roles';
import { getError } from '../../utils';

const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';

const FIELD_LABELS = {
	name: 'Nombre',
	email: 'Email',
	role: 'Rol',
};

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true, error: '' };
	case 'FETCH_SUCCESS':
		return { ...state, user: action.payload, loading: false };
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'SAVE_REQUEST':
		return { ...state, saving: true };
	case 'SAVE_SUCCESS':
		return { ...state, saving: false };
	case 'SAVE_FAIL':
		return { ...state, saving: false };
	default:
		return state;
	}
};

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

function UserEditor({ mode, userId, onCancel, onSaved, saveLabel }) {
	const isEdit = mode === 'edit';
	const { dispatch: ctxDispatch } = useContext(Store);
	const [{ loading, saving, error: loadError, user }, dispatch] = useReducer(reducer, {
		user: {},
		loading: isEdit,
		saving: false,
		error: '',
	});

	const [name, setName] = useState('');
	const [email, setEmail] = useState('');
	const [phone, setPhone] = useState('');
	const [role, setRole] = useState('employee');
	const [signature, setSignature] = useState(null);
	const [signatureFile, setSignatureFile] = useState(null);
	const [uploading, setUploading] = useState(false);
	const [fieldErrors, setFieldErrors] = useState({});
	const [formError, setFormError] = useState('');

	useEffect(() => {
		if (!isEdit || !userId) {
			return;
		}

		let isCurrent = true;
		const fetchUser = async () => {
			dispatch({ type: 'FETCH_REQUEST' });
			try {
				const result = await userService.getDetails(userId);
				if (!isCurrent) return;
				dispatch({ type: 'FETCH_SUCCESS', payload: result.data });
				setName(result.data.name || '');
				setEmail(result.data.email || '');
				setPhone(result.data.phone || '');
				setRole(result.data.role || 'employee');
				setSignature(result.data.signature || null);
			} catch (err) {
				if (isCurrent) dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
			}
		};
		fetchUser();
		return () => {
			isCurrent = false;
		};
	}, [isEdit, userId]);

	const clearFieldError = (field) => {
		setFieldErrors((prevErrors) => {
			if (!prevErrors[field]) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors[field];
			return nextErrors;
		});
	};

	const validate = () => {
		const errors = {};
		if (!name.trim()) errors.name = REQUIRED_FIELD_MESSAGE;
		if (!email.trim()) errors.email = REQUIRED_FIELD_MESSAGE;
		if (!role) errors.role = REQUIRED_FIELD_MESSAGE;
		return errors;
	};

	const showMissingFields = (errors) => {
		const missingLabels = Object.keys(errors).map((field) => FIELD_LABELS[field] || field);
		const message = `Complete el/los siguiente/s campo/s requerido/s: ${missingLabels.join(', ')}.`;
		setFormError(message);
		toast.error(message);
	};

	const uploadSignature = async () => {
		if (!signatureFile || signatureFile.length === 0) {
			toast.warning('Debe seleccionar un archivo');
			return;
		}

		const file = signatureFile[0];
		const formData = new FormData();
		const suffix = Math.random().toString(36).substring(7);
		formData.append('file', file, (file.name || '').replaceAll(' ', '') + suffix);
		if (signature && signature._id) {
			formData.append('documentId', signature._id);
		}

		setUploading(true);
		try {
			const response = await documentService.upload(formData, 'signature');
			setSignature(response.data);
			setSignatureFile(null);
			toast.success('Firma subida correctamente');
		} catch (err) {
			toast.error(getError(err));
		} finally {
			setUploading(false);
		}
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (saving || uploading) {
			return;
		}

		const errors = validate();
		if (Object.keys(errors).length > 0) {
			setFieldErrors(errors);
			showMissingFields(errors);
			return;
		}

		const payload = {
			name: name.trim(),
			email: email.trim().toLowerCase(),
			phone: phone.trim(),
			role,
		};

		if (isEdit) {
			payload.signature = signature?._id || signature;
		} else {
			payload.password = '123456';
		}

		try {
			dispatch({ type: 'SAVE_REQUEST' });
			const response = isEdit
				? await userService.edit(user._id || userId, payload)
				: await userService.create(payload);
			dispatch({ type: 'SAVE_SUCCESS' });
			setFormError('');
			setFieldErrors({});
			if (isEdit) {
				ctxDispatch({ type: 'USER_UPDATE_SUCCESS', payload: response.data });
				localStorage.setItem('user', JSON.stringify(response.data));
				toast.success('Usuario actualizado Correctamente');
			} else {
				toast.success('Usuario creado');
			}
			onSaved(response.data?._id || userId);
		} catch (err) {
			dispatch({ type: 'SAVE_FAIL' });
			toast.error(getError(err));
		}
	};

	if (loading) {
		return <DetailPageLoader entityLabel='usuario' />;
	}

	return (
		<Form id='user-editor-form' onSubmit={handleSubmit} className='tw:space-y-4'>
			<div className='tw:sticky tw:top-14 tw:z-20 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface/95 tw:px-5 tw:py-3.5 tw:shadow-sm tw:backdrop-blur'>
				<div className='tw:text-[12.5px] tw:text-ink-2'>
					{isEdit ? 'Actualizá los datos principales del usuario.' : 'Completá los datos para crear el usuario.'}
				</div>
				<div className='tw:flex tw:gap-2'>
					<Button icon={<MdArrowBack />} onClick={onCancel} disabled={saving || uploading}>
						Cancelar
					</Button>
					<Button
						variant='primary'
						icon={isEdit ? <MdSave /> : <MdLibraryAdd />}
						type='submit'
						disabled={saving || uploading}
					>
						{saving ? 'Guardando...' : saveLabel}
					</Button>
				</div>
			</div>

			{(formError || loadError) && (
				<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
					{formError || loadError}
				</div>
			)}

			<Section title='Datos del usuario'>
				<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
					<FormField id='user-name' label={FIELD_LABELS.name} required>
						<Form.Control
							id='user-name'
							className={controlClass}
							value={name}
							disabled={saving || uploading}
							onChange={(event) => {
								setName(event.target.value);
								clearFieldError('name');
							}}
						/>
						<FieldError message={fieldErrors.name} />
					</FormField>
					<FormField id='user-email' label={FIELD_LABELS.email} required>
						<Form.Control
							id='user-email'
							type='email'
							className={controlClass}
							value={email}
							disabled={saving || uploading}
							onChange={(event) => {
								setEmail(event.target.value.toLowerCase());
								clearFieldError('email');
							}}
						/>
						<FieldError message={fieldErrors.email} />
					</FormField>
					<FormField id='user-phone' label='Teléfono'>
						<Form.Control
							id='user-phone'
							className={controlClass}
							value={phone}
							disabled={saving || uploading}
							onChange={(event) => setPhone(event.target.value)}
						/>
					</FormField>
					<FormField id='user-role' label={FIELD_LABELS.role} required>
						<Select
							id='user-role'
							options={ROLE_OPTIONS}
							value={role}
							disabled={saving || uploading}
							onChange={(event) => {
								setRole(event.target.value);
								clearFieldError('role');
							}}
						/>
						<FieldError message={fieldErrors.role} />
					</FormField>
				</div>
			</Section>

			{isEdit && (
				<Section title='Firma'>
					<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-[minmax(0,1fr)_auto] md:tw:items-end'>
						<FormField id='user-signature' label='Archivo de firma'>
							<Form.Control
								id='user-signature'
								type='file'
								accept='image/*,.pdf'
								className={controlClass}
								disabled={saving || uploading}
								onChange={(event) => setSignatureFile(event.target.files)}
							/>
							{signature?.fileLink && (
								<a
									className='tw:mt-2 tw:inline-block tw:text-[13px] tw:text-accent-ink tw:hover:text-accent'
									href={signature.fileLink}
									target='_blank'
									rel='noopener noreferrer'
								>
									Ver firma actual
								</a>
							)}
						</FormField>
						<Button
							icon={<MdOutlineFileUpload />}
							onClick={uploadSignature}
							disabled={saving || uploading || !signatureFile}
						>
							{uploading ? 'Subiendo...' : 'Subir'}
						</Button>
					</div>
				</Section>
			)}
		</Form>
	);
}

UserEditor.propTypes = {
	mode: PropTypes.oneOf(['create', 'edit']).isRequired,
	userId: PropTypes.string,
	onCancel: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired,
	saveLabel: PropTypes.string.isRequired,
};

export default UserEditor;
